import { Controller } from '@hotwired/stimulus'
import Dropdown, { type DropdownOptions } from '@/lib/dropdown'

export default class extends Controller<HTMLElement> {
  static values = {
    config: { type: Object, default: {} }
  }

  declare readonly configValue: DropdownOptions
  declare dropdown: Dropdown
  declare skipBlurCommit?: boolean
  declare searchEdited?: boolean

  connect() {
    // Building the wrapper moves a <select> into it, which disconnects and reconnects this
    // controller: the second connect finds the dropdown already set up.
    if (Dropdown.for(this.element)) return

    if (!this.element.classList.contains('selection')) {
      this.element.classList.add('dropdown--from-button')
    }

    // Read before a <select> hands its classes over to the wrapper.
    const blurOnChange = this.element.tagName !== 'SELECT' && this.element.classList.contains('search')

    this.dropdown = new Dropdown(this.element, {
      ...this.config(),
      onShow: () => setTimeout(() => this.adjustDirection(), 0),
      onChange: () => {
        if (blurOnChange) (document.activeElement as HTMLElement).blur()
      }
    })
    if (!this.dropdown.menu) return

    if (this.dropdown.module.classList.contains('allow-additions')) {
      if (this.dropdown.isMultiple) {
        this.setupMultipleAllowAdditions()
      } else {
        this.setupComboSelect()
      }
    }
  }

  get search(): HTMLInputElement {
    return this.dropdown.search!
  }

  // Multiple `allow-additions` commits an explicit non-empty search term on blur, never the
  // highlighted item: a forced selection would add a contact by accident.
  setupMultipleAllowAdditions() {
    this.dropdown.setting('forceSelection', false)

    this.dropdown.menu.addEventListener('mousedown', () => {
      this.skipBlurCommit = true
    })
    this.search.addEventListener('blur', () => {
      if (this.skipBlurCommit) {
        this.skipBlurCommit = false
        return
      }

      const value = this.search.value.trim()
      if (value) this.dropdown.setSelected(value)
    })
  }

  // Single `allow-additions` dropdowns behave as a combobox: the search input always shows
  // the committed value, and whatever it contains on blur or Enter becomes the selection
  // (an emptied input clears it).
  setupComboSelect() {
    this.dropdown.setting('forceSelection', false)
    this.dropdown.setting('keepSearchTerm', true)

    // Only the select: the search input fires its own native change on blur, which must not
    // resync the old selection.
    this.dropdown.input!.addEventListener('change', () => this.syncSearchInput())
    this.syncSearchInput()

    // Capture runs before the dropdown's own Enter handling, which would pick the
    // highlighted suggestion instead of the typed text.
    this.search.addEventListener(
      'keydown',
      (event: KeyboardEvent) => {
        if (event.key !== 'Enter') return

        event.preventDefault()
        event.stopImmediatePropagation()
        this.commitSearchValue()
        this.dropdown.hide()
      },
      { capture: true }
    )
    this.search.addEventListener('click', () => this.search.select())
    // Only user edits fire `input`: an emptied input clears the selection right away.
    this.search.addEventListener('input', () => {
      this.searchEdited = true
      if (!this.search.value) this.commitSearchValue()
    })
    // A menu click blurs the search input first: let the dropdown handle it alone.
    this.dropdown.menu.addEventListener('mousedown', () => {
      this.skipBlurCommit = true
    })
    this.search.addEventListener('focus', () => {
      this.skipBlurCommit = false
      this.searchEdited = false
    })
    this.search.addEventListener('blur', () => {
      if (this.skipBlurCommit) {
        this.skipBlurCommit = false
      } else if (this.search.value || this.searchEdited) {
        this.commitSearchValue()
      } else {
        this.syncSearchInput()
      }
    })
  }

  // A suggestion picked with the arrows wins over the text typed to reach it.
  commitSearchValue() {
    const choice = this.dropdown.keyboardChoice()
    const value = choice ? this.dropdown.itemValue(choice) : this.search.value.trim()

    if (value !== this.selectedValue()) {
      if (value) {
        this.dropdown.setSelected(value)
      } else {
        this.dropdown.clear()
      }
    }

    this.syncSearchInput()
  }

  syncSearchInput() {
    const value = this.selectedValue()
    this.search.value = value
    this.dropdown.text?.classList.toggle('filtered', !!value)
  }

  selectedValue() {
    return this.dropdown.values()[0] || ''
  }

  adjustDirection() {
    const { module, menu } = this.dropdown
    if ('dropdownNoUpward' in module.dataset || 'dropdownNoUpward' in this.element.dataset) return
    if (this.configValue.direction === 'upward' || !menu.offsetHeight) return

    const rect = module.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const isInModal = module.closest('[data-controller~="ui--modal-component"]')

    module.classList.toggle('upward', (spaceBelow < menu.offsetHeight && spaceAbove > menu.offsetHeight) || !!isInModal)
  }

  config(): DropdownOptions {
    return {
      ...(this.element.classList.contains('dropdown-with-blank') ? { placeholder: false } : {}),
      ...(this.element.classList.contains('allow-additions')
        ? {
            allowAdditions: true,
            hideAdditions: !((this.element as HTMLSelectElement).multiple || this.element.classList.contains('multiple'))
          }
        : {}),
      ...this.configValue
    }
  }
}
